import { Box } from '@mui/material';
import { CsvExportParams } from 'ag-grid-community';
import FlexBox from 'Components/FlexBox';
import { ThemeButton, ThemeTypography } from 'DesignSystem';
import { closeOpenWktGeometries } from 'DesignSystem/Map/utils/map.utils';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import useRawMaterials from 'FeatureModules/ComponentChain/hooks/useRawMaterials';
import { IRawMaterial } from 'FeatureModules/ComponentChain/types/component.types';
import CropCellEditor from 'FeatureModules/DataImport/Components/DataImportTable/CellEditors/CropCellEditor';
import GeometryCellEditor from 'FeatureModules/DataImport/Components/DataImportTable/CellEditors/GeometryCellEditor';
import LandOwnershipCellEditor from 'FeatureModules/DataImport/Components/DataImportTable/CellEditors/LandOwnershipCellEditor';
import DropdownCellRenderer from 'FeatureModules/DataImport/Components/DataImportTable/CellRenderers/DropdownCellRenderer';
import ExcelTable, {
  IExcelTableProps,
  IGridApiHandle,
} from 'FeatureModules/DataImport/Components/DataImportTable/ExcelTable';
import {
  TCellValue,
  TColDefWithValidator,
  TColumnDefinition,
} from 'FeatureModules/DataImport/Components/DataImportTable/excelTable.types';
import HeaderColumn from 'FeatureModules/DataImport/Components/DataImportTable/HeaderColumn';
import {
  IOriginTableSettings,
  TColumnKeys,
} from 'FeatureModules/DataImport/types/dataImport.types';
import {
  DatasetRow,
  EDatasetColumn,
  EDatasetIssueSeverityLevel,
  EIDatasetCellIssueCode,
  TDatasetTableRepresentation,
  TIDatasetCellIssueCode,
} from 'FeatureModules/DataImport/types/dataset.types';
import { createColumnDefinition } from 'FeatureModules/DataImport/utils/dataImport.utils';
import {
  createPositiveFloatValidator,
  createPositiveIntegerValidator,
  getOpenFirstAndLastPointsFromWKT,
  validateCrop,
  validateGeometry,
  validateLandOwnership,
  validateNotEmpty,
} from 'FeatureModules/DataImport/utils/dataImport.validators';
import {
  DatasetColumnName,
  DatasetColumnSampleData,
  matchColumnKeyToColumnMapping,
  transformDataForDisplay,
} from 'FeatureModules/DataImport/utils/dataset.utils';
import { InlineHelperText } from 'FeatureModules/ProductPage/Components/InlineHelperText/InlineHelperText';
import {
  LandOwnershipDescriptions,
  LandOwnershipLabels,
} from 'FeatureModules/Site/utils/site.utils';
import { IDatasetMappedColumn } from 'graphql/secure/mutations/types/dataset-mutation.types';
import React, {
  FC,
  ForwardedRef,
  memo,
  MouseEvent,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import useMeasure from 'react-use/lib/useMeasure';
import { INITIAL_TABLE_ROWS } from 'utils/pagination.utils';
import { v4 as uuid } from 'uuid';
import IssueBanner from '../IssueBanner';

export interface IDatasetTableHandle {
  getDataAsCsv?: (
    params?: CsvExportParams & { removeFirstAndLastColumn?: boolean; removeEmptyRows?: boolean }
  ) => string;
  runValidations?: () => boolean;
  getColumnsMapping: () => IDatasetMappedColumn[];
  resetOpenGeometryErrors: () => void;
}

interface IDatasetValidationProps {
  datasetTableRef?: ForwardedRef<IDatasetTableHandle>;
  tableRepresentation?: TDatasetTableRepresentation;
  mode: IExcelTableProps<TOriginDatasetTableRowData, unknown>['mode'];
  tableSettings?: IOriginTableSettings;
  height?: number | string;
  onClickDownload: () => void;
}

export type TOriginDatasetTableRowData = {
  id: string;
  farmId?: TColumnDefinition<string>;
  plotId: TColumnDefinition<string>;
  plotName?: TColumnDefinition<string>;
  plotSize?: TColumnDefinition<number>;
  originClusterId?: TColumnDefinition<string>;
  farmName?: TColumnDefinition<string>;
  geometry: TColumnDefinition<string>;
  farmSize?: TColumnDefinition<number>;
  numberOfFarmers?: TColumnDefinition<number>;
  additionalCrops?: TColumnDefinition<string>;
  landOwnership?: TColumnDefinition<string>;
  farmOwnerFullName?: TColumnDefinition<string>;
  farmOwnerFirstName?: TColumnDefinition<string>;
  farmOwnerLastName?: TColumnDefinition<string>;
  siteDescription?: TColumnDefinition<string>;
};

export interface IOriginDatasetTableContext {
  settings?: IOriginTableSettings;
  rawMaterials: IRawMaterial[];
  selectMultipleRawMaterials: boolean;
}

interface IValidationIssue {
  message: string;
  issueCode?: TIDatasetCellIssueCode;
  count: number;
  locations: { rowIndex: number; columnName: keyof DatasetRow }[];
}

const DatasetValidation: FC<IDatasetValidationProps> = ({
  datasetTableRef,
  mode,
  tableSettings,
  tableRepresentation,
  onClickDownload,
  height = 500,
}) => {
  const [currentErrorIndex, setCurrentErrorIndex] = useState<number>(0);
  const [closedAllOpenGeometries, setClosedAllOpenGeometries] = useState<boolean>(false);

  const [containerRef, { height: containerHeight }] = useMeasure<HTMLDivElement>();
  const gridRef = useRef<IGridApiHandle<TOriginDatasetTableRowData>>(null);
  const { rawMaterials } = useRawMaterials();

  const checkIfColumnIsEnabledInSettings = (columnKey: TColumnKeys) =>
    tableSettings?.columns &&
    columnKey in tableSettings.columns &&
    tableSettings.columns[columnKey as TColumnKeys]?.enabled;

  const createRequiredRowDataColumns = (): TOriginDatasetTableRowData => ({
    id: uuid(),
    geometry: { value: '' },
    plotId: { value: '' },
  });

  const hasOpenGeometries = useMemo(
    () =>
      tableRepresentation?.rows?.some(row =>
        row.geometryWkt.issues?.some(({ issueCode }) => issueCode === EIDatasetCellIssueCode.E14)
      ) || false,
    [tableRepresentation]
  );

  /**
   * Build issue list based on the issues in the table representation. We separate errors, warnings and infos and count the number of occurrences of each unique issue to be able to display this in the issue banner. We also save the location of each issue to be able to navigate to the next issue in the table when clicking on the issue in the banner.
   */
  const issues:
    | {
        errors: IValidationIssue[];
        warnings: IValidationIssue[];
        infos: IValidationIssue[];
      }
    | undefined = useMemo(
    () =>
      tableRepresentation?.rows?.reduce(
        (prevIssues, row, rowIndex) => {
          const columnKeys = Object.keys(row) as (keyof DatasetRow)[];

          return columnKeys.reduce((prevRowIssues, columnName) => {
            const cell = row[columnName];
            // Separate errors, warnings and infos based on the severity level of the issue
            cell?.issues?.forEach(issue => {
              // Find correct array to push the issue based on the severity level of the issue
              let issueList;
              if (issue.severityLevel === EDatasetIssueSeverityLevel.ERROR) {
                issueList = prevRowIssues.errors;
              } else if (issue.severityLevel === EDatasetIssueSeverityLevel.WARNING) {
                issueList = prevRowIssues.warnings;
              } else {
                issueList = prevRowIssues.infos;
              }

              const existingIssue = issueList.find(
                ({ message, issueCode }) =>
                  message === issue.issueMessage && issueCode === issue.issueCode
              );
              // If the same error already exists for another cell, we increase the count and add the new location, otherwise we create a new error entry
              if (existingIssue) {
                issueList.push({
                  ...existingIssue,
                  locations: [...existingIssue.locations, { rowIndex, columnName }],
                  count: existingIssue.count + 1,
                });
              } else {
                // If the issue does not exist yet we create a new entry in the issue list
                issueList.push({
                  issueCode: issue.issueCode,
                  message: issue.issueMessage,
                  count: 1,
                  locations: [{ rowIndex, columnName }],
                });
              }
            });

            return prevRowIssues;
          }, prevIssues);
        },
        {
          errors: [] as IValidationIssue[],
          warnings: [] as IValidationIssue[],
          infos: [] as IValidationIssue[],
        }
      ),
    [tableRepresentation]
  );

  const hasPlotIdErrorCode = useMemo(
    () => issues?.errors.some(({ issueCode }) => issueCode === EIDatasetCellIssueCode.E12),
    [issues?.errors]
  );

  const hasOtherErrorCodes = useMemo(
    () =>
      issues?.errors.some(
        ({ issueCode }) =>
          issueCode !== EIDatasetCellIssueCode.E14 && issueCode !== EIDatasetCellIssueCode.E12
      ),
    [issues?.errors]
  );

  /**
   * Column field keys @type TOriginDatasetTableRowData should match the keys in the table settings @type TColumnKeys
   */
  const columns: TColDefWithValidator<TOriginDatasetTableRowData>[] = useMemo(() => {
    const allColumnDefinitions: TColDefWithValidator<TOriginDatasetTableRowData>[] = [
      createColumnDefinition(
        'originClusterId',
        () => (
          <HeaderColumn
            title={DatasetColumnName[EDatasetColumn.ORIGIN_CLUSTER_ID]}
            tooltip={{
              helperText:
                'Unique ID if you want to cluster your farms. The id can reference an existing cluster in the platform, otherwise a new cluster will be created with that ID. All farms that have this cluster ID will be grouped together.',
            }}
          />
        ),
        undefined,
        undefined,
        undefined,
        false
      ),
      createColumnDefinition(
        'farmId',
        () => (
          <HeaderColumn
            title={DatasetColumnName[EDatasetColumn.FARM_ID]}
            tooltip={{
              helperText:
                'Unique ID for the farm. Makes it possible to update the farm data on future imports.',
            }}
          />
        ),
        undefined,
        undefined,
        undefined,
        false
      ),
      createColumnDefinition(
        'farmName',
        () => (
          <HeaderColumn
            title={DatasetColumnName[EDatasetColumn.FARM_NAME]}
            tooltip={{
              helperText:
                'Agricultural farm name, if not specified in the import the name will be set to "Unnamed Farm".',
            }}
          />
        ),
        undefined,
        undefined,
        undefined,
        false
      ),
      createColumnDefinition(
        'plotId',
        () => (
          <HeaderColumn
            isRequired
            title={DatasetColumnName[EDatasetColumn.PLOT_ID]}
            tooltip={{
              helperText:
                'Unique ID for the plot within the farm. Makes it possible to update the plot data on future imports.',
            }}
          />
        ),
        validateNotEmpty,
        undefined,
        undefined,
        true
      ),
      createColumnDefinition(
        'plotName',
        () => (
          <HeaderColumn
            title={DatasetColumnName[EDatasetColumn.PLOT_NAME]}
            tooltip={{
              helperText:
                "Plot name, if not specified in the import the name will be set to 'Unnamed Plot'.",
            }}
          />
        ),
        undefined,
        undefined,
        undefined,
        false
      ),
      createColumnDefinition(
        'geometry',
        () => (
          <HeaderColumn
            isRequired
            title={
              tableSettings?.columns?.geometry?.format
                ? DatasetColumnName[tableSettings.columns.geometry.format]
                : DatasetColumnName[EDatasetColumn.GEOMETRY_WKT]
            }
            tooltip={{
              helperText:
                tableSettings?.columns?.geometry?.format === EDatasetColumn.GEOMETRY_WKT ? (
                  <>
                    Well-known text (WKT) is a text markup language for
                    representing vector geometry objects. You can add and edit plot geo-data in the
                    following formats:
                    <ul style={{ paddingLeft: 24, margin: 0 }}>
                      <li>POINT (30 10)</li>
                      <li>POLYGON ((35 10, 10 20, 35 10), (20 30, 35 20, 20 30))</li>
                      <li>
                        MULTIPOLYGON (((40 40, 40 30, 40 40)), ((20 35, 10 30, 10 10, 30 5, 45 20,
                        20 35), (30 20, 20 15, 20 25, 30 20)))
                      </li>
                    </ul>
                    Read more about WKT format{' '}
                    <a
                      href="https://en.wikipedia.org/wiki/Well-known_text_representation_of_geometry"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      here
                    </a>
                  </>
                ) : (
                  'The two coordinates need to be separated by a comma or space while the latitude needs to be in range of -90 to +90 and the longitude in range of -180 to +180.'
                ),
            }}
          />
        ),
        validateGeometry,
        undefined,
        {
          cellEditor:
            tableSettings?.columns?.geometry?.format === EDatasetColumn.GEOMETRY_WKT
              ? memo(GeometryCellEditor)
              : undefined,
        }
      ),
      createColumnDefinition(
        'plotSize',
        () => (
          <HeaderColumn
            title={
              tableSettings?.columns?.plotSize?.format
                ? DatasetColumnName[tableSettings.columns.plotSize.format]
                : 'Plot size'
            }
            tooltip={{
              helperText:
                'Size of the plot in the specified unit. This only needs to be provided if the plot geometry is a point. Otherwise the size will be calculated based on the provided geometry.',
            }}
          />
        ),
        createPositiveFloatValidator(
          `Plot size needs to be a positive number, in e.g. ${
            tableSettings?.columns?.plotSize?.format
              ? DatasetColumnSampleData[tableSettings.columns.plotSize.format]
              : '4.0 ha '
          }.`
        ),
        undefined,
        undefined,
        false
      ),
      createColumnDefinition(
        'farmSize',
        () => (
          <HeaderColumn
            title={
              tableSettings?.columns?.farmSize?.format
                ? DatasetColumnName[tableSettings.columns.farmSize.format]
                : 'Farm size'
            }
            tooltip={{
              helperText:
                'Size of the farm in the specified unit. This includes the area of all plots within the farm plus any additional area that is part of the farm but not mapped as plots.',
            }}
          />
        ),
        createPositiveFloatValidator(
          `Farm size needs to be a positive number, in e.g. ${
            tableSettings?.columns?.farmSize?.format
              ? DatasetColumnSampleData[tableSettings.columns.farmSize.format]
              : '4.0 ha '
          }.`
        ),
        undefined,
        undefined,
        false
      ),
      createColumnDefinition(
        'numberOfFarmers',
        () => <HeaderColumn title={DatasetColumnName[EDatasetColumn.NUMBER_OF_FARMERS]} />,
        createPositiveIntegerValidator(`Needs to be a positive integer number, eg 2`),
        undefined,
        undefined,
        false
      ),
      createColumnDefinition(
        'additionalCrops',
        () => <HeaderColumn title={DatasetColumnName[EDatasetColumn.ADDITIONAL_CROPS]} />,
        validateCrop,
        DropdownCellRenderer,
        {
          cellEditor: memo(CropCellEditor),
          // onCellClicked: startEditingCell, // Workaround to fix opening the cell editor on double click
        },
        false
      ),
      createColumnDefinition(
        'landOwnership',
        () => (
          <HeaderColumn
            title={DatasetColumnName[EDatasetColumn.LAND_OWNERSHIP]}
            tooltip={{
              helperText: (
                <Box display="flex" flexDirection="column" gap={1}>
                  The Land ownership describes the type of ownership over the land. The options are:
                  {Object.entries(LandOwnershipLabels).map(([key, value]) => (
                    <ThemeTypography key={key} variant="BODY_MEDIUM">
                      <b>{value}:</b> {LandOwnershipDescriptions[key]}
                    </ThemeTypography>
                  ))}
                </Box>
              ),
            }}
          />
        ),
        validateLandOwnership,
        DropdownCellRenderer,
        {
          cellEditor: memo(LandOwnershipCellEditor),
        },
        false
      ),
      createColumnDefinition(
        'farmOwnerFullName',
        () => <HeaderColumn title={DatasetColumnName[EDatasetColumn.FARM_OWNER_FULL_NAME]} />,
        undefined,
        undefined,
        undefined,
        false
      ),
      createColumnDefinition(
        'farmOwnerFirstName',
        () => <HeaderColumn title={DatasetColumnName[EDatasetColumn.FARM_OWNER_FIRST_NAME]} />,
        undefined,
        undefined,
        undefined,
        false
      ),
      createColumnDefinition(
        'farmOwnerLastName',
        () => <HeaderColumn title={DatasetColumnName[EDatasetColumn.FARM_OWNER_LAST_NAME]} />,
        undefined,
        undefined,
        undefined,
        false
      ),
      createColumnDefinition(
        'siteDescription',
        () => <HeaderColumn title={DatasetColumnName[EDatasetColumn.SITE_DESCRIPTION]} />,
        undefined,
        undefined,
        undefined,
        false
      ),
    ];

    return allColumnDefinitions.filter(
      ({ field }) => field && checkIfColumnIsEnabledInSettings(field as TColumnKeys)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRepresentation?.columnsNames, tableSettings]);

  const rowData = useMemo(() => {
    if (!tableRepresentation) {
      return [...Array(INITIAL_TABLE_ROWS).keys()].map(() => createRequiredRowDataColumns());
    } else {
      return [...tableRepresentation.rows.map(row => transformDataForDisplay(row, tableSettings))];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRepresentation]); // The row data is not dependent on tableSettings otherwise we would override changes made in the table data

  const getColumnsMapping: () => IDatasetMappedColumn[] = useCallback(() => {
    const keys =
      gridRef.current?.getColumnKeys({
        removeFirstAndLastColumn: true,
      }) || [];
    return keys.map(key => ({
      sourceColumnName: key,
      targetColumnName: matchColumnKeyToColumnMapping(key, tableSettings),
    }));
  }, [gridRef, tableSettings]);

  const scrollToNextError = useCallback(async () => {
    const api = gridRef.current?.getApi();
    if (!api) return;

    const issues: { rowIndex: number; columnName: string }[] = [];
    await api.forEachNode(node => {
      const data = node.data;
      if (!data || node.rowIndex === null) return;
      const rowData = Object.entries(data) as [string, TColumnDefinition<TCellValue>][];
      const cellIssues = rowData
        .map(([key, cell]) => {
          if (key !== 'id' && (cell?.errorMessage !== undefined || !cell?.isValid)) {
            return {
              rowIndex: node.rowIndex,
              columnName: key,
            };
          }
          return null;
        })
        .filter(Boolean) as { rowIndex: number; columnName: string }[];
      issues.push(...cellIssues);
    });

    if (issues.length > 0 && currentErrorIndex < issues.length) {
      const { rowIndex, columnName } = issues[currentErrorIndex];
      api.setFocusedCell(rowIndex, columnName);
      api.ensureIndexVisible(rowIndex, 'middle');
      setCurrentErrorIndex(prev => prev + 1);
    } else {
      setCurrentErrorIndex(0);
      api.ensureIndexVisible(0, 'middle');
      setCurrentErrorIndex(0);
    }
  }, [currentErrorIndex, setCurrentErrorIndex]);

  const handleCloseAllOpenGeometries = useCallback(() => {
    if (gridRef.current) {
      setClosedAllOpenGeometries(true);
      const closedGeometriesColumns = gridRef.current.getColumnValues('geometry').map(cell => {
        if (cell) {
          const wktGeometry = cell.toString();
          const isGeometryOpen = getOpenFirstAndLastPointsFromWKT(wktGeometry).length > 0;
          if (isGeometryOpen) {
            return closeOpenWktGeometries(wktGeometry);
          }
        }
        return cell;
      });
      gridRef.current.setColumnValues('geometry', closedGeometriesColumns);
    }
  }, [gridRef]);

  const duplicatePlotIdToFarmId = useCallback(
    (event: MouseEvent | KeyboardEvent) => {
      event.stopPropagation();
      const api = gridRef.current?.getApi();
      if (!api) return;

      api.forEachNode(node => {
        if (!node.data) return;

        const farmIdCell = node.data.farmId;
        const plotIdCell = node.data.plotId;

        // Only fill farmId if it's empty and plotId has a value
        const farmIdValue = farmIdCell?.value?.toString()?.trim();
        const plotIdValue = plotIdCell?.value?.toString()?.trim();

        if (!farmIdValue && plotIdValue) {
          node.setData({
            ...node.data,
            farmId: {
              ...farmIdCell,
              value: plotIdValue,
              touched: true,
            },
          });
        }
      });

      api.refreshCells({ columns: ['farmId'], force: true });
    },
    [gridRef]
  );

  const duplicateFarmIdToPlotId = useCallback(
    (event: MouseEvent | KeyboardEvent) => {
      event.stopPropagation();
      const api = gridRef.current?.getApi();
      if (!api) return;

      api.forEachNode(node => {
        if (!node.data) return;

        const plotIdCell = node.data.plotId;
        const farmIdCell = node.data.farmId;

        // Only fill plotId if it's empty and farmId has a value
        const plotIdValue = plotIdCell?.value?.toString()?.trim();
        const farmIdValue = farmIdCell?.value?.toString()?.trim();

        if (!plotIdValue && farmIdValue) {
          node.setData({
            ...node.data,
            plotId: {
              ...plotIdCell,
              value: farmIdValue,
              touched: true,
            },
          });
        }
      });

      api.refreshCells({ columns: ['plotId'], force: true });
      // Rerun validations to update the error state of the cells after auto-filling the plot IDs
      gridRef.current?.runValidations();
    },
    [gridRef]
  );

  useImperativeHandle(
    datasetTableRef,
    () => ({
      getDataAsCsv: gridRef.current?.getDataAsCsv,
      runValidations: gridRef.current?.runValidations,
      getColumnsMapping,
      resetOpenGeometryErrors: () => setClosedAllOpenGeometries(false),
    }),
    [gridRef, getColumnsMapping]
  );

  return (
    <>
      <FlexBox ref={containerRef} flexDirection="column" alignItems="normal" gap={2}>
        <FlexBox flexDirection="column" alignItems="normal" gap={2}>
          {!!issues?.errors.length && (
            <IssueBanner
              count={issues.errors.length}
              summaryTitle="processing error"
              severity="error"
              content={
                <Box display="flex" flexDirection="column" gap={2}>
                  {hasOtherErrorCodes && (
                    <Box>
                      <ThemeTypography variant="BODY_MEDIUM" color="BLACK">
                        Multiple errors have been detected in the dataset. These errors need to be
                        resolved to ensure the quality of the data and a successful import.
                      </ThemeTypography>
                      <ThemeTypography variant="BODY_MEDIUM" color="BLACK">
                        Please review and correct these issues in the table before importing.
                      </ThemeTypography>
                    </Box>
                  )}
                  {!closedAllOpenGeometries && hasOpenGeometries && (
                    <Box>
                      <ThemeTypography variant="BODY_MEDIUM">
                        In WKT-formatted polygon (Well-known text representation of geometry), t he
                        first and last coordinate within a polygon must be the same, to indicate
                        that a polygon shape is closed.
                      </ThemeTypography>
                      <FlexBox gap={2}>
                        <ThemeTypography variant="BODY_MEDIUM">
                          You can here choose to automatically close all open polygons. This is a
                          good idea if you know it is a mistake in the formatting. However, If the
                          polygons are in fact not complete and missing more coordinates, the
                          geo-data might be incorrect and you will then need to address it before
                          re-uploading.
                        </ThemeTypography>
                        <ThemeButton
                          startIcon={<Icon name="polygon-mapping" />}
                          color="WHITE"
                          size="small"
                          onClick={handleCloseAllOpenGeometries}
                        >
                          Close all open polygons
                        </ThemeButton>
                      </FlexBox>
                    </Box>
                  )}
                  {hasPlotIdErrorCode && (
                    <Box>
                      <ThemeTypography variant="BODY_MEDIUM" color="BLACK">
                        Having a Plot ID per plot is required, to avoid duplicate data and allow
                        tracking which farm each plot belongs to. This ensures that you refer to the
                        same plots in your internal systems as in this system when importing and
                        exporting geo-data in the future.
                      </ThemeTypography>
                      <FlexBox gap={2}>
                        <ThemeTypography variant="BODY_MEDIUM" color="BLACK">
                          You can click here to duplicate the Farm ID as Plot ID for each plot. Note
                          that this assumes there being one plot per farm. If that is not the case,
                          please ensure that Plot IDs are collected, noting that multiple plots may
                          be associated with a single farm.
                        </ThemeTypography>
                        <ThemeButton
                          startIcon={<Icon name="copy" />}
                          color="WHITE"
                          size="small"
                          onClick={duplicateFarmIdToPlotId}
                        >
                          For each missing, duplicate Farm ID to use as Plot ID
                        </ThemeButton>
                      </FlexBox>
                    </Box>
                  )}
                </Box>
              }
            />
          )}
          {!!issues?.warnings.length && (
            <IssueBanner
              count={issues.warnings.length}
              severity="warning"
              summaryTitle="processing warning"
              AdditionalSummary={
                <ThemeButton
                  startIcon={<Icon name="copy" />}
                  color="WHITE"
                  size="small"
                  onClick={duplicatePlotIdToFarmId}
                >
                  For each missing, duplicate Plot ID to use as Farm ID
                </ThemeButton>
              }
              content={
                <Box display="flex" flexDirection="column" gap={2}>
                  <ThemeTypography variant="BODY_MEDIUM" color="BLACK">
                    Having a Farm ID per plot is not required but recommended to avoid duplicate
                    data and allow tracking which farm each plot belongs to.
                  </ThemeTypography>
                  <ThemeTypography variant="BODY_MEDIUM" color="BLACK">
                    You can click here to duplicate the Plot ID as Farm ID for each plot.
                  </ThemeTypography>
                  <ThemeTypography variant="BODY_MEDIUM" color="BLACK">
                    Once saved with assigned IDs, make sure to then download the geo-data to import
                    the IDs to your internal systems. This ensures that you refer to the same plots
                    in your internal systems as in this system when importing and exporting geo-data
                    in the future.
                  </ThemeTypography>
                </Box>
              }
            />
          )}
          {!!issues?.infos.length && (
            <IssueBanner
              summaryTitle="info message"
              count={issues.infos.length}
              severity="info"
              content={
                <Box display="flex" flexDirection="column" gap={2}>
                  <ThemeTypography variant="BODY_MEDIUM" color="BLACK">
                    Some plot IDs or/and farm IDs are already used inside the platform. This can
                    lead to data being overwritten if the same IDs are used for different plots or
                    farms, but it can also be intentional if you want to update existing plots or
                    farms by reusing the same IDs. Please check the IDs in the file and make sure
                    they are correct before importing.
                  </ThemeTypography>
                </Box>
              }
            />
          )}
        </FlexBox>

        <FlexBox gap={2} mb={2}>
          {!!issues?.errors.length && (
            <ThemeButton color="WHITE" startIcon={<Icon name="next" />} onClick={scrollToNextError}>
              Jump to next error in table
            </ThemeButton>
          )}
          <ThemeButton color="WHITE" startIcon={<Icon name="download" />} onClick={onClickDownload}>
            Download file with IDs and highlighted issues
          </ThemeButton>

          <InlineHelperText
            helperText="What are plots, farms and clusters?"
            variant="INFO"
            hoverText={
              <>
                <p>
                  Plots represents the specific land area used to cultivate certain raw materials
                  and can be mapped as a single coordinate point or a polygon shape, indicating the
                  plot location and boundaries. Multiple plots can belong to the same farm, or there
                  can be one plot per farm. This can vary between different origins.
                </p>
                <p>
                  Farms can in turn be clustered together in relation to their shared geographical
                  region or their legal connection (e.g. a farming community). Using clusters is
                  helpful when managing different origins.
                </p>
                <p>
                  Farms and clusters can be linked to a company as the data owner, meaning the legal
                  entity responsible for each. Assigning data owner to each farm or cluster also
                  helps to manage your origins per supplier.
                </p>
                <p>
                  Farms can also be linked to a certain Farm owner name, which is referring to the
                  individual who legally owns a farm at the origin. Multiple farms can be linked to
                  the same farm owner name.
                </p>
              </>
            }
          />
        </FlexBox>
      </FlexBox>

      {/* Subtract the top container height - 16px margin & padding to use the available height for the table */}
      <Box height={`calc(100% - ${containerHeight}px - 16px)`} minHeight={600}>
        <ExcelTable<TOriginDatasetTableRowData, IOriginDatasetTableContext>
          mode={mode}
          height={height}
          gridRef={gridRef}
          additionalContextValues={{
            settings: tableSettings,
            rawMaterials,
            selectMultipleRawMaterials: true,
          }}
          rowData={rowData}
          columnDefs={columns}
        />
      </Box>
    </>
  );
};

export default DatasetValidation;
